import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  type ActionUpdate,
  ActionsService,
  type ApiError,
  type CommitmentGoalUpdate,
  CommitmentGoalsService,
  type CommitmentUpdate,
  CommitmentsService,
  type IndicatorUpdate,
  IndicatorsService,
  type PillarUpdate,
  PillarsService,
} from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

export function useKanbanMutations(slug: string) {
  const queryClient = useQueryClient();
  const showToast = useCustomToast();

  function makeMutation<TVariables, TData = unknown>(
    mutationFn: MutationFunction<TData, TVariables>,
    successMsg: string,
    key: string[]
  ) {
    return useMutation<TData, ApiError, TVariables>({
      mutationFn,
      onSuccess: async () => {
        showToast('Succès', successMsg, 'success');
        await queryClient.invalidateQueries({ queryKey: key });
      },
      onError: (err: ApiError) => {
        handleError(err, showToast);
      },
    });
  }

  return {
    mutatePillar: makeMutation(
      ({ id, data }: { id: string; data: PillarUpdate }) =>
        PillarsService.updatePillar({ companySlug: slug, requestBody: data, id }),
      'Pilier réordonné',
      ['csrPlans']
    ),
    mutateCommitment: makeMutation(
      ({ id, data }: { id: string; data: CommitmentUpdate }) =>
        CommitmentsService.updateCommitment({ companySlug: slug, requestBody: data, id }),
      'Commitment réordonné',
      ['csrPlans']
    ),
    mutateAction: makeMutation(
      ({ id, data }: { id: string; data: ActionUpdate }) =>
        ActionsService.updateAction({ companySlug: slug, requestBody: data, id }),
      'Action réordonné',
      ['commitments_actions']
    ),
    mutateIndicator: makeMutation(
      ({ id, data }: { id: string; data: IndicatorUpdate }) =>
        IndicatorsService.updateIndicator({ companySlug: slug, requestBody: data, id }),
      'Indicateur réordonné',
      ['commitments_indicators']
    ),
    mutateGoal: makeMutation(
      ({ id, data }: { id: string; data: CommitmentGoalUpdate }) =>
        CommitmentGoalsService.updateGoal({ companySlug: slug, requestBody: data, id }),
      'Objectif réordonné',
      ['commitments_goals']
    ),
  };
}
